/**
 * Copilot session: the signed-in CMS user's RebelMouse cross-auth JWT.
 *
 * The rebelboost pages proxy (reverse-proxied on every runner under
 * /agentic/platform) accepts that JWT as a Bearer token: it verifies it
 * against the site's JWKS, exchanges it server-side for a platform session
 * and runs the turn as the real user at the site's own property — the
 * `property` claim names it. That is what lets platform-side tools (the
 * Client Backends MCP) act as the user, which the previous anonymous
 * `/api/chat/init` session could not. Same flow as the entry editor's chat
 * widget (enterprise-chat-ui chatBootSession).
 *
 * The monolith's CSRF middleware never enforces the token on this POST, so a
 * same-origin JSON request with the session cookie is enough.
 */

export const COPILOT_TOKEN_URL = '/core/users/oauth2/cross_auth_token';
export const COPILOT_PLATFORM_BASE_PATH = '/agentic/platform';

// Refresh this long before the JWT expires so an in-flight run never loses
// its SSE stream to an expired token.
const EXPIRY_MARGIN_MS = 60 * 1000;

export class CopilotSessionError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'CopilotSessionError';
    this.status = status;
  }
}

function base64UrlDecode(segment) {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  try {
    return decodeURIComponent(
      binary
        .split('')
        .map(char => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join('')
    );
  } catch {
    return binary;
  }
}

/** The JWT's payload as an object; throws on a malformed token. */
export function decodeJwtPayload(token) {
  const parts = typeof token === 'string' ? token.split('.') : [];
  if (parts.length !== 3) {
    throw new CopilotSessionError('Cross-auth token is not a JWT');
  }
  return JSON.parse(base64UrlDecode(parts[1]));
}

/** A session object from a cross-auth JWT; throws when a claim is missing. */
export function sessionFromToken(token, now = Date.now()) {
  const claims = decodeJwtPayload(token);
  const propertyId = claims.property;
  if (typeof propertyId !== 'string' || propertyId.length === 0) {
    throw new CopilotSessionError(
      'This site is not registered on the platform yet (the cross-auth token carries no property).'
    );
  }
  const expiresAtMs =
    typeof claims.exp === 'number' ? claims.exp * 1000 : now + 5 * 60 * 1000;
  return {
    token,
    propertyId,
    siteId: typeof claims.site_domain === 'string' ? claims.site_domain : '',
    userId: claims.sub === undefined ? null : String(claims.sub),
    email: typeof claims.email === 'string' ? claims.email : '',
    chatApiUrl: `${COPILOT_PLATFORM_BASE_PATH}/api/properties/${encodeURIComponent(propertyId)}/pages`,
    expiresAt: new Date(expiresAtMs).toISOString(),
  };
}

export function isSessionFresh(session, now = Date.now()) {
  if (!session || typeof session.expiresAt !== 'string' || !session.token) {
    return false;
  }
  const expiresAt = Date.parse(session.expiresAt);
  return Number.isFinite(expiresAt) && expiresAt - EXPIRY_MARGIN_MS > now;
}

function sessionMessage(status) {
  if (status === 401 || status === 403) {
    return 'Sign in to RebelMouse to use Copilot.';
  }
  if (status === 404) {
    return 'Copilot is not available on this site (cross-auth is not configured).';
  }
  return `Copilot session failed: ${status}`;
}

export async function fetchCrossAuthToken() {
  let res;
  try {
    res = await fetch(COPILOT_TOKEN_URL, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });
  } catch (error) {
    throw new CopilotSessionError(`Copilot session failed: ${error.message || error}`, 0);
  }
  if (!res.ok) {
    throw new CopilotSessionError(sessionMessage(res.status), res.status);
  }
  const payload = await res.json();
  if (!payload || typeof payload.token !== 'string' || payload.token.length === 0) {
    throw new CopilotSessionError('Cross-auth token response did not include a token.', res.status);
  }
  return payload.token;
}

/**
 * Keyed by workflow slug and token URL so a session minted for another
 * endpoint (the retired anonymous init) is never reused.
 */
export function sessionStorageKey(workflowSlug) {
  return `rm_copilot_session:${workflowSlug}:${COPILOT_TOKEN_URL}`;
}

function readCached(key, now) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }
    const cached = JSON.parse(raw);
    if (isSessionFresh(cached, now)) {
      return cached;
    }
    localStorage.removeItem(key);
  } catch {
    /* storage unavailable or corrupt */
  }
  return null;
}

function writeCached(key, session) {
  try {
    localStorage.setItem(key, JSON.stringify(session));
  } catch {
    /* storage unavailable */
  }
}

const inflight = new Map();

/**
 * The current session, minted on first use and refreshed a minute before the
 * JWT expires. Concurrent callers share one in-flight mint.
 */
export function getCopilotSession(workflowSlug, now = Date.now()) {
  const key = sessionStorageKey(workflowSlug);
  const cached = readCached(key, now);
  if (cached) {
    return Promise.resolve(cached);
  }
  if (inflight.has(key)) {
    return inflight.get(key);
  }
  const minted = fetchCrossAuthToken()
    .then(token => {
      const session = sessionFromToken(token, now);
      writeCached(key, session);
      return session;
    })
    .finally(() => inflight.delete(key));
  inflight.set(key, minted);
  return minted;
}

export function clearCopilotSession(workflowSlug) {
  try {
    localStorage.removeItem(sessionStorageKey(workflowSlug));
  } catch {
    /* storage unavailable */
  }
}
